import React, { useState, useCallback } from "react";
import { Card } from "./Shell";
import { PEAK_STATE_FULLSCREEN_URL } from "../lib/maya";

/**
 * PeakStateSection — embeddable section that lives inside the Welcome page
 * (between the Ecosystem section and the wellness disclaimer). Members
 * scroll into Peak State as the second wellness program on the same page.
 *
 * Compliance: NON-MEDICAL WELLNESS throughout. Allowed wellness/focus
 * language; prohibited clinical/diagnostic/condition language. Disclaimer
 * uses spec-provided wording verbatim — including necessary negation of
 * "medical / neurological / mental-health" to scope what Peak State is NOT.
 */

// Dedicated Peak State check-in form (JotForm, IWMC-owned).
const PEAK_STATE_CHECKIN_URL =
  "https://integratedwellness.jotform.com/261513760940051";

const HOW_IT_WORKS = [
  {
    n: "01",
    title: "Enter the zone.",
    copy:
      "A guided sequence — slow breathing, a settling of the eyes and attention — brings you to calm-alert focus, the relaxed-but-ready state.",
  },
  {
    n: "02",
    title: "Rehearse your moment.",
    copy:
      "Guided by vivid, first-person mental imagery, you rehearse performing at your best — seeing it, feeling it, from the inside.",
  },
  {
    n: "03",
    title: "Anchor it.",
    copy:
      "You capture the cue that gets you there, so you can find the state again on your own.",
  },
  {
    n: "04",
    title: "(Optional) See it on EEG.",
    copy:
      "In an in-house session at IWMC, a wellness brain sensor lets you watch your relaxed-focus activity respond as you settle — turning an inner state into something you can actually see.",
  },
];

const INSIDE_SESSION = [
  {
    code: "PS.01",
    title: "Calm-alert breathing",
    copy: "Slow, even breathing that brings focus without tension.",
  },
  {
    code: "PS.02",
    title: "Settling the gaze & attention",
    copy: "A steady, relaxed focus, like the quiet moment before a shot.",
  },
  {
    code: "PS.03",
    title: "Vivid mental rehearsal",
    copy: "First-person, full-sensory imagery of performing at your best.",
  },
  {
    code: "PS.04",
    title: "Performance anchor",
    copy:
      "A personal cue and a confident, forward-looking image to carry in.",
  },
  {
    code: "PS.05",
    title: "Optional EEG biofeedback",
    copy:
      "See your relaxed-focus brain activity respond, in-house at IWMC.",
  },
];

const WHAT_TO_EXPECT = [
  "Sessions are short — usually 10 to 20 minutes.",
  "Guided step-by-step by a warm AI coach.",
  "Practice on your own anytime; add an in-house EEG session when you want to see your state.",
  "Great before competitions, presentations, performances, or any high-focus moment.",
];

const FAQ = [
  {
    q: "Is this medical or a brain test?",
    a: "No. It's a wellness focus-training program. The optional EEG is a wellness biofeedback experience that shows your brain activity — it isn't a medical test, diagnosis, or brain-health assessment.",
  },
  {
    q: "Do I need the EEG?",
    a: "No. The guided sessions work on their own; the in-house EEG is an optional add-on to see your state.",
  },
  {
    q: "How long is a session?",
    a: "Usually 10 to 20 minutes.",
  },
  {
    q: "Does it guarantee better performance?",
    a: "No honest program can promise that. Peak State trains a focus state many people find valuable; individual results vary.",
  },
  {
    q: "What if I'm having a hard time right now?",
    a: "If you're in crisis or thinking about harming yourself, call or text 988, or call 911. This isn't a crisis service.",
  },
];

export default function PeakStateSection() {
  // Primary CTA — open the dedicated Peak State check-in form in a new tab.
  const handleCheckIn = useCallback(() => {
    window.open(
      PEAK_STATE_CHECKIN_URL,
      "_blank",
      "noopener,noreferrer"
    );
  }, []);

  // Open the Peak State Coach in a new tab. We deliberately skip the
  // `wakePeakState` agent-registry helper here: since only Maya is mounted
  // as a floating launcher on this page, the helper's first probe step
  // (JotForm's global `AgentInitializer.open()`) sometimes claims success
  // without actually opening anything when given an un-initialized agent
  // ID. A direct `window.open(fullScreenUrl, "_blank")` is unambiguous.
  const [coachHint, setCoachHint] = useState(false);
  const handleWakeCoach = useCallback(() => {
    window.open(
      PEAK_STATE_FULLSCREEN_URL,
      "_blank",
      "noopener,noreferrer"
    );
    setCoachHint(true);
    setTimeout(() => setCoachHint(false), 2400);
  }, []);

  return (
    <section
      id="peak-state"
      className="mt-24 fade-up"
      data-testid="peak-state-section"
    >
      {/* ───────────── Mega-eyebrow + hero (lives inline with NSR) ───────────── */}
      <div className="iw-section-head">
        <div className="iw-section-head__label">§ 10 — Peak State · Sibling Program</div>
        <h2 className="iw-section-head__title">
          Train the calm, focused "in-the-zone" state.
        </h2>
      </div>

      {/* HERO */}
      <div className="grid lg:grid-cols-[1.05fr_1fr] gap-10 lg:gap-16 items-center">
        <div className="fade-up order-2 lg:order-1">

          <div className="mb-5 flex flex-wrap items-center gap-2">
            <span
              className="iw-mono uppercase px-2.5 py-1"
              style={{
                background: "var(--iw-accent-red)",
                color: "#fff",
                borderRadius: "2px",
                fontSize: "10px",
                letterSpacing: "0.20em",
                fontWeight: 600,
              }}
              title="Wellness focus-training program — not medical care"
              data-testid="peak-state-wellness-tag"
            >
              Wellness · Non-Medical
            </span>
            <span
              className="iw-mono uppercase px-2.5 py-1"
              style={{
                background: "var(--iw-surface)",
                color: "var(--iw-primary)",
                border: "1px solid var(--iw-border)",
                borderRadius: "2px",
                fontSize: "10px",
                letterSpacing: "0.20em",
                fontWeight: 600,
              }}
            >
              Focus · Composure · Readiness
            </span>
          </div>

          <h3
            className="serif"
            style={{
              fontSize: "clamp(2.2rem, 5vw, 3.75rem)",
              lineHeight: 1.02,
              color: "var(--iw-primary)",
            }}
          >
            Peak{" "}
            <span style={{ color: "var(--iw-accent-red)" }}>State.</span>
          </h3>

          <p
            className="mt-6 max-w-xl text-lg sm:text-xl leading-relaxed"
            style={{ color: "var(--iw-body)" }}
          >
            Train the calm, focused{" "}
            <strong style={{ color: "var(--iw-primary)" }}>
              "in-the-zone"
            </strong>{" "}
            state — the same relaxed-alert mindset athletes reach before the
            big moment — through guided breathing and vivid mental rehearsal.
          </p>
          <p
            className="mt-3 max-w-xl text-base sm:text-lg leading-relaxed"
            style={{ color: "var(--iw-muted)" }}
          >
            See it come alive with an optional in-house brain-activity
            session.
          </p>

          <div className="mt-7 flex items-center gap-3 flex-wrap">
            <button
              type="button"
              className="iw-btn-primary"
              onClick={handleCheckIn}
              data-testid="peak-state-checkin-btn"
            >
              Start with a quick check-in <span aria-hidden="true">↗</span>
            </button>
            <button
              type="button"
              className="iw-btn-ghost"
              onClick={handleWakeCoach}
              data-testid="peak-state-hero-wake-coach-btn"
            >
              Talk to the Coach <span aria-hidden="true">↗</span>
            </button>
            <a
              href="#peak-how-it-works"
              className="iw-mono uppercase inline-flex items-center"
              style={{
                color: "var(--iw-muted)",
                fontSize: "11px",
                letterSpacing: "0.18em",
                fontWeight: 600,
                textDecoration: "none",
              }}
              data-testid="peak-state-how-link"
            >
              See how it works ↓
            </a>
          </div>

          <div
            className="mt-5 text-sm leading-relaxed max-w-xl"
            style={{ color: "var(--iw-muted)" }}
          >
            Short, guided sessions. Optional in-house brain-activity session.
            Stoppable at any time. Members only — non-medical throughout.
          </div>
          </div>

          {/* Hero visual block */}
          <div className="order-1 lg:order-2 fade-up">
            <PeakHeroVisual />
          </div>
        </div>

        {/* ────────── Peak State Coach · Inline CTA (lives right under the
             hero so members can talk to the coach before reading the rest) ────────── */}
        <PeakStateWarmup />
        <section
          id="peak-state-coach"
          className="mt-12 fade-up"
          data-testid="peak-state-coach-section"
        >
          <div
            className="iw-card relative overflow-hidden p-7 sm:p-10"
            style={{ borderTop: "3px solid var(--iw-accent-red)" }}
          >
            <div className="grid md:grid-cols-[1.4fr_1fr] gap-8 md:gap-10 items-center">
              <div>
                <div
                  className="iw-mono uppercase mb-3"
                  style={{
                    color: "var(--iw-accent-red)",
                    fontSize: "11px",
                    letterSpacing: "0.24em",
                    fontWeight: 600,
                  }}
                >
                  AI Peak State Coach · Wellness
                </div>
                <h3
                  className="serif leading-tight"
                  style={{
                    color: "var(--iw-primary)",
                    fontWeight: 700,
                    fontSize: "clamp(1.75rem, 3.2vw, 2.5rem)",
                  }}
                >
                  Talk it through with the Peak State Coach.
                </h3>
                <p
                  className="text-[15px] sm:text-base mt-3 leading-relaxed max-w-xl"
                  style={{ color: "var(--iw-body)" }}
                >
                  Curious what a session feels like, or whether it's a fit for
                  your next big moment? The AI Peak State Coach can walk you
                  through the calm-alert focus state, guide a short
                  rehearsal, and answer questions in plain language. Strictly
                  non-medical wellness coaching.
                </p>
                <div className="mt-7 flex flex-col sm:flex-row items-start sm:items-center gap-3">
                  <button
                    type="button"
                    onClick={handleWakeCoach}
                    className="iw-btn-primary"
                    data-testid="peak-state-wake-coach-btn"
                  >
                    Wake the Coach <span aria-hidden="true">↗</span>
                  </button>
                  <a
                    href={PEAK_STATE_FULLSCREEN_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="iw-btn-ghost"
                    data-testid="peak-state-coach-open-fullscreen"
                  >
                    Open in new tab ↗
                  </a>
                </div>
                <div
                  className="iw-mono uppercase mt-5"
                  style={{
                    color: coachHint ? "var(--iw-accent-red)" : "var(--iw-muted)",
                    fontSize: "10px",
                    letterSpacing: "0.22em",
                    fontWeight: 600,
                    minHeight: "1.1em",
                    transition: "color 200ms ease",
                  }}
                  aria-live="polite"
                >
                  {coachHint
                    ? "Opening in a new tab ↗"
                    : "A focused chat — opens in a new tab so it doesn't crowd this page."}
                </div>
              </div>

              {/* Right column — wordmark "presence" tile, mirrors Elara's
                  card pattern but uses the Peak State name. No third-party
                  avatar so we stay in our design system. */}
              <div className="hidden md:block">
                <div
                  className="relative aspect-square w-full max-w-[280px] ml-auto"
                  style={{
                    background:
                      "radial-gradient(circle at 35% 30%, rgba(217,4,41,0.14), rgba(30,58,138,0.18) 55%, rgba(17,41,107,0.05) 100%)",
                    border: "1px solid var(--iw-border)",
                    borderRadius: "2px",
                  }}
                  aria-hidden="true"
                >
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="iw-maya-ring iw-maya-ring--3" />
                    <span className="iw-maya-ring iw-maya-ring--2" />
                    <span className="iw-maya-ring iw-maya-ring--1" />
                    <span
                      className="relative serif text-center leading-none"
                      style={{
                        color: "var(--iw-primary)",
                        fontSize: "clamp(1.9rem, 3.2vw, 2.75rem)",
                        fontWeight: 700,
                        letterSpacing: "-0.02em",
                        textShadow: "0 4px 18px rgba(17,41,107,0.18)",
                      }}
                    >
                      Peak<br />State
                    </span>
                  </div>
                  <span
                    className="absolute top-3 left-3 iw-mono uppercase px-2 py-1"
                    style={{
                      background: "var(--iw-accent-red)",
                      color: "#fff",
                      borderRadius: "2px",
                      fontSize: "9px",
                      letterSpacing: "0.18em",
                      fontWeight: 600,
                    }}
                  >
                    Live · Focus Coach
                  </span>
                  <span
                    className="absolute bottom-3 right-3 iw-mono uppercase inline-flex items-center gap-1.5 px-2 py-1"
                    style={{
                      background: "rgba(255,255,255,0.92)",
                      border: "1px solid var(--iw-border)",
                      borderRadius: "2px",
                      color: "var(--iw-primary)",
                      fontSize: "9px",
                      letterSpacing: "0.18em",
                      fontWeight: 600,
                    }}
                  >
                    <span className="iw-now-dot" /> Listening
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ──────────────────── § 01 — WHAT IT IS ──────────────────── */}
        <section id="what-it-is" className="mt-24 sm:mt-32 fade-up">
          <div className="iw-section-head">
            <div className="iw-section-head__label">§ 01 — What it is</div>
            <h2 className="iw-section-head__title">
              A practiced focus state — not a stretch goal.
            </h2>
            <p
              className="text-base sm:text-lg max-w-3xl"
              style={{ color: "var(--iw-body)" }}
            >
              Athletes describe their best moments as being "in the zone" —
              calm, clear, fully absorbed, relaxed but switched on. That state
              has a signature in the brain, and it can be practiced. Peak State
              guides you into it: first settling into a calm-alert focus, then
              vividly rehearsing the moment you want to perform at your best.
              It's mental training for focus and composure — useful before
              anything that matters, from a presentation to a competition to a
              hard conversation.
            </p>
          </div>
        </section>

        {/* ──────────────── § 02 — HOW IT WORKS ──────────────── */}
        <section id="peak-how-it-works" className="mt-24 fade-up">
          <div className="iw-section-head">
            <div className="iw-section-head__label">§ 02 — How it works</div>
            <h2 className="iw-section-head__title">A two-part session.</h2>
            <p
              className="text-base sm:text-lg max-w-2xl"
              style={{ color: "var(--iw-body)" }}
            >
              Settle into the relaxed-alert state first. Then rehearse the
              moment you want to perform at your best.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {HOW_IT_WORKS.map((s) => (
              <StepCard key={s.n} step={s} />
            ))}
          </div>
        </section>

        {/* ─────────── § 03 — INSIDE A SESSION ─────────── */}
        <section id="inside" className="mt-24 fade-up">
          <div className="iw-section-head">
            <div className="iw-section-head__label">
              § 03 — Inside a Session
            </div>
            <h2 className="iw-section-head__title">
              The five building blocks.
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {INSIDE_SESSION.map((t) => (
              <InsideCard key={t.code} {...t} />
            ))}
          </div>
        </section>

        {/* ─────────── § 04 — WHAT TO EXPECT ─────────── */}
        <section id="what-to-expect" className="mt-24 fade-up">
          <div className="iw-section-head">
            <div className="iw-section-head__label">
              § 04 — What to expect
            </div>
            <h2 className="iw-section-head__title">
              Short, guided, repeatable.
            </h2>
          </div>
          <ul
            className="grid sm:grid-cols-2 gap-3 list-none m-0 p-0"
            data-testid="peak-state-expect-list"
          >
            {WHAT_TO_EXPECT.map((line) => (
              <li
                key={line}
                className="iw-card p-5 sm:p-6 flex gap-3 items-start"
                style={{ borderLeft: "3px solid var(--iw-accent-red)" }}
              >
                <span
                  className="iw-mono flex-shrink-0 mt-0.5"
                  style={{
                    color: "var(--iw-accent-red)",
                    fontSize: "14px",
                    fontWeight: 700,
                  }}
                  aria-hidden="true"
                >
                  →
                </span>
                <span
                  className="text-[15px] leading-relaxed"
                  style={{ color: "var(--iw-primary)" }}
                >
                  {line}
                </span>
              </li>
            ))}
          </ul>
        </section>

        {/* ────────── § 05 — BRAIN ACTIVITY (EEG) ────────── */}
        <section id="brain-activity" className="mt-24 fade-up">
          <div className="iw-section-head">
            <div className="iw-section-head__label">
              § 05 — Brain Activity · Optional, In-House
            </div>
            <h2 className="iw-section-head__title">
              Curious what "in the zone" looks like for you?
            </h2>
          </div>
          <div className="grid md:grid-cols-[1.05fr_1fr] gap-6 items-stretch">
            <div
              className="relative overflow-hidden"
              style={{
                border: "1px solid var(--iw-border)",
                borderRadius: "2px",
                background: "#f7f5f0",
                minHeight: 360,
              }}
              data-testid="peak-state-eeg-visual"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_ecstatic-wilson-4/artifacts/7d2kg9ms_mXn5OUh-YUH-Nw2B.png"
                alt="A translucent body silhouette with the nervous system illuminated alongside biometric holographic readouts — wellness biofeedback in-house at IWMC"
                className="block w-full h-full object-cover"
                loading="lazy"
              />
              <div
                className="absolute top-4 left-4 px-2.5 py-1"
                style={{
                  background: "rgba(11, 21, 48, 0.78)",
                  backdropFilter: "blur(8px)",
                  border: "1px solid rgba(255,255,255,0.20)",
                  borderRadius: "2px",
                }}
              >
                <span
                  className="iw-mono uppercase"
                  style={{
                    color: "#fff",
                    fontSize: "10px",
                    letterSpacing: "0.22em",
                    fontWeight: 600,
                  }}
                >
                  Wellness Biofeedback · In-House
                </span>
              </div>
            </div>
            <Card divider>
              <p
                className="text-base sm:text-lg leading-relaxed mb-4"
                style={{ color: "var(--iw-body)" }}
              >
                In an in-house session, a comfortable wellness brain sensor reads
                your relaxed-focus activity while the coach guides you, so you
                can watch your state shift in real time and track it over visits.
              </p>
              <p
                className="text-base leading-relaxed"
                style={{ color: "var(--iw-body)" }}
              >
                It's a wellness experience to help you learn your own signals —
                not a medical test or diagnosis, and everyone's brain responds a
                little differently.
              </p>
              <div
                className="mt-5 inline-flex items-start gap-2.5 px-3 py-2"
                style={{
                  background: "var(--iw-blue-tint)",
                  border: "1px solid var(--iw-blue)",
                  borderRadius: "2px",
                }}
                data-testid="peak-state-eeg-honesty-note"
              >
                <span
                  className="iw-mono uppercase flex-shrink-0 mt-0.5"
                  style={{
                    color: "var(--iw-blue)",
                    fontSize: "10px",
                    letterSpacing: "0.22em",
                    fontWeight: 600,
                  }}
                >
                  Honest-measurement note
                </span>
                <span
                  className="text-[12.5px] leading-relaxed"
                  style={{ color: "var(--iw-primary)" }}
                >
                  EEG here is wellness biofeedback — a signal that reflects
                  relaxed-focus brain activity. Not a medical test, not a
                  diagnostic readout, and individual responses vary.
                </span>
              </div>
            </Card>
          </div>
        </section>

        {/* ────────── § 06 — WHO IT'S FOR ────────── */}
        <section id="peak-who" className="mt-24 fade-up">
          <div className="iw-section-head">
            <div className="iw-section-head__label">§ 06 — Who it's for</div>
            <h2 className="iw-section-head__title">
              For anyone who needs to show up.
            </h2>
          </div>
          <div className="grid md:grid-cols-[1.4fr_1fr] gap-6">
            <Card divider>
              <p
                className="text-base sm:text-lg leading-relaxed mb-4"
                style={{ color: "var(--iw-body)" }}
              >
                For anyone who wants sharper focus and steadier composure when
                it counts — athletes, performers, professionals, students.
              </p>
              <p
                className="text-base leading-relaxed"
                style={{ color: "var(--iw-body)" }}
              >
                A quick check-in first helps make sure it's the right fit; if
                something else would serve you better, our team will help you
                find it.
              </p>
            </Card>
            <div
              className="p-6"
              style={{
                background: "var(--iw-blue-tint)",
                border: "1px solid var(--iw-blue)",
                borderRadius: "2px",
              }}
              data-testid="peak-state-eligibility-note"
            >
              <div
                className="iw-mono uppercase mb-2"
                style={{
                  color: "var(--iw-blue)",
                  fontSize: "10px",
                  letterSpacing: "0.22em",
                  fontWeight: 600,
                }}
              >
                Eligibility · Quick Check-in
              </div>
              <p
                className="text-[15px] leading-relaxed"
                style={{ color: "var(--iw-primary)" }}
              >
                A warm, non-clinical check-in — under a minute. Some members
                are guided to other forms of support; that's a feature, not a
                barrier.
              </p>
            </div>
          </div>
        </section>

        {/* ────────── § 07 — FAQ ────────── */}
        <section id="peak-faq" className="mt-24 fade-up">
          <div className="iw-section-head">
            <div className="iw-section-head__label">§ 07 — FAQ</div>
            <h2 className="iw-section-head__title">The honest answers.</h2>
          </div>
          <div
            style={{
              border: "1px solid var(--iw-border)",
              borderRadius: "2px",
              background: "var(--iw-surface)",
            }}
            data-testid="peak-state-faq-list"
          >
            {FAQ.map((item, i) => (
              <FAQItem
                key={item.q}
                n={String(i + 1).padStart(2, "0")}
                q={item.q}
                a={item.a}
                last={i === FAQ.length - 1}
              />
            ))}
          </div>
        </section>

        {/* Repeat CTA */}
        <section className="mt-20 fade-up">
          <div
            className="p-7 sm:p-9 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6"
            style={{
              background: "linear-gradient(135deg, #11296b 0%, #1e3a8a 100%)",
              borderRadius: "2px",
              boxShadow: "0 18px 50px -20px rgba(17, 41, 107, 0.35)",
            }}
          >
            <div>
              <div
                className="iw-mono uppercase mb-2"
                style={{
                  color: "#fca5a5",
                  fontSize: "11px",
                  letterSpacing: "0.22em",
                  fontWeight: 600,
                }}
              >
                Ready when you are
              </div>
              <div
                className="serif text-2xl sm:text-3xl"
                style={{ color: "#fff" }}
              >
                Ready to find your zone?
              </div>
            </div>
            <button
              type="button"
              className="iw-btn-primary"
              onClick={handleCheckIn}
              data-testid="peak-state-cta-repeat-btn"
            >
              Start with a quick check-in <span aria-hidden="true">↗</span>
            </button>
          </div>
        </section>
    </section>
  );
}

/* ─────────────────────────── Helpers ─────────────────────────── */

function PeakHeroVisual() {
  return (
    <div className="relative">
      <div
        className="absolute -inset-4"
        style={{
          background:
            "radial-gradient(60% 50% at 50% 50%, rgba(30,58,138,0.20), rgba(217,4,41,0.08) 55%, transparent 70%)",
          filter: "blur(24px)",
        }}
        aria-hidden="true"
      />
      <div
        className="relative overflow-hidden"
        style={{
          border: "1px solid var(--iw-border)",
          borderRadius: "2px",
          boxShadow:
            "0 30px 80px -30px rgba(17, 41, 107, 0.40)",
          background: "#0b1530",
        }}
        data-testid="peak-state-hero-visual"
      >
        <img
          src="https://customer-assets.emergentagent.com/job_ecstatic-wilson-4/artifacts/fllwdx32_UfiTk-JtUmSeCnF4.png"
          alt="Athlete in motion with the body's energy pathways and brain illuminated — the calm, alert, focused state Peak State trains"
          className="block w-full h-auto peak-hero-image"
          style={{ display: "block" }}
          loading="lazy"
        />
        {/* Top-left mono badge — preserves the IW system overlay */}
        <div
          className="absolute top-4 left-4 px-2.5 py-1"
          style={{
            background: "rgba(11, 21, 48, 0.78)",
            backdropFilter: "blur(8px)",
            border: "1px solid rgba(255,255,255,0.18)",
            borderRadius: "2px",
          }}
        >
          <span
            className="iw-mono uppercase"
            style={{
              color: "#fca5a5",
              fontSize: "10px",
              letterSpacing: "0.22em",
              fontWeight: 600,
            }}
          >
            Calm · Alert · Focused
          </span>
        </div>
        {/* Top-right live dot */}
        <div
          className="absolute top-4 right-4 px-2.5 py-1 inline-flex items-center gap-1.5"
          style={{
            background: "rgba(11, 21, 48, 0.78)",
            backdropFilter: "blur(8px)",
            border: "1px solid rgba(255,255,255,0.18)",
            borderRadius: "2px",
          }}
        >
          <span className="iw-now-dot" />
          <span
            className="iw-mono uppercase"
            style={{
              color: "rgba(255,255,255,0.92)",
              fontSize: "9px",
              letterSpacing: "0.20em",
              fontWeight: 600,
            }}
          >
            In the zone
          </span>
        </div>
        {/* Bottom caption */}
        <div
          className="absolute left-0 right-0 bottom-0 p-5 sm:p-6"
          style={{
            background:
              "linear-gradient(to top, rgba(11,21,48,0.92) 0%, rgba(11,21,48,0.55) 60%, transparent 100%)",
          }}
        >
          <div
            className="serif"
            style={{
              color: "#fff",
              fontSize: "clamp(1.5rem, 2.6vw, 2.2rem)",
              lineHeight: 1.05,
              fontWeight: 700,
            }}
          >
            The state before<br />
            <span style={{ color: "#fca5a5" }}>the shot.</span>
          </div>
        </div>
      </div>
      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <Pill>Breathing</Pill>
        <Pill>Gaze</Pill>
        <Pill>Rehearsal</Pill>
        <Pill>Anchor</Pill>
        <Pill>EEG · Optional</Pill>
      </div>
    </div>
  );
}

function StepCard({ step }) {
  return (
    <div
      className="iw-card iw-step-card p-5 sm:p-6 relative"
      style={{ borderTop: "3px solid var(--iw-accent-red)" }}
      data-testid={`peak-state-step-${step.n}`}
    >
      <div
        className="iw-mono mb-3"
        style={{
          color: "var(--iw-accent-red)",
          fontSize: "11px",
          letterSpacing: "0.18em",
          fontWeight: 600,
        }}
      >
        Step {step.n}
      </div>
      <div
        className="serif text-lg mb-2"
        style={{ color: "var(--iw-primary)", fontWeight: 700 }}
      >
        {step.title}
      </div>
      <p
        className="text-[14px] leading-relaxed"
        style={{ color: "var(--iw-body)" }}
      >
        {step.copy}
      </p>
    </div>
  );
}

function InsideCard({ code, title, copy }) {
  return (
    <div
      className="iw-card p-6 sm:p-7 flex flex-col"
      style={{ borderTop: "3px solid var(--iw-accent-red)" }}
      data-testid={`peak-inside-${code.toLowerCase()}`}
    >
      <span
        className="iw-mono uppercase mb-2"
        style={{
          color: "var(--iw-muted)",
          fontSize: "10px",
          letterSpacing: "0.18em",
          fontWeight: 600,
        }}
      >
        {code}
      </span>
      <h3
        className="serif text-xl mb-2"
        style={{ color: "var(--iw-primary)", fontWeight: 700 }}
      >
        {title}
      </h3>
      <p
        className="text-[14.5px] leading-relaxed"
        style={{ color: "var(--iw-body)" }}
      >
        {copy}
      </p>
    </div>
  );
}

function FAQItem({ n, q, a, last }) {
  const [open, setOpen] = useState(false);
  return (
    <button
      type="button"
      onClick={() => setOpen((v) => !v)}
      className="w-full text-left px-5 sm:px-7 py-5 transition-colors"
      style={{
        borderBottom: last ? "none" : "1px solid var(--iw-border)",
        background: open ? "var(--iw-blue-tint)" : "transparent",
      }}
      data-testid={`peak-state-faq-${n}`}
      aria-expanded={open}
    >
      <div className="flex items-start gap-4">
        <span
          className="iw-mono flex-shrink-0"
          style={{
            color: "var(--iw-accent-red)",
            fontSize: "12px",
            letterSpacing: "0.16em",
            fontWeight: 600,
          }}
        >
          {n}
        </span>
        <div className="flex-1">
          <div
            className="serif text-lg sm:text-xl flex items-start justify-between gap-4"
            style={{ color: "var(--iw-primary)", fontWeight: 700 }}
          >
            <span>{q}</span>
            <span
              aria-hidden="true"
              style={{
                color: "var(--iw-accent-red)",
                fontSize: "20px",
                lineHeight: 1,
                transform: open ? "rotate(45deg)" : "rotate(0)",
                transition: "transform 200ms ease",
              }}
            >
              +
            </span>
          </div>
          {open && (
            <p
              className="mt-3 text-base leading-relaxed"
              style={{ color: "var(--iw-body)" }}
            >
              {a}
            </p>
          )}
        </div>
      </div>
    </button>
  );
}

function Pill({ children }) {
  return (
    <span
      className="iw-mono uppercase px-2.5 py-1"
      style={{
        border: "1px solid var(--iw-border)",
        borderRadius: "2px",
        background: "var(--iw-surface)",
        color: "var(--iw-primary)",
        fontSize: "10px",
        letterSpacing: "0.18em",
        fontWeight: 600,
      }}
    >
      {children}
    </span>
  );
}

export function PeakStateWarmup() {
  const [intent, setIntent] = useState("");
  const [state, setState] = useState("idle"); // idle | loading | done | error
  const [reply, setReply] = useState("");
  const [memberName, setMemberName] = useState("");
  const [saveState, setSaveState] = useState("idle"); // idle | saving | saved | saved-local | error
  const [saveError, setSaveError] = useState("");

  const submit = useCallback(async (e) => {
    e.preventDefault();
    if (intent.trim().length < 3 || state === "loading") return;
    setState("loading");
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/peak-state/warmup`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ intent: intent.trim() }),
        }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setReply(data.reply || "");
      setState("done");
      setSaveState("idle");
      setSaveError("");
    } catch (err) {
      setState("error");
    }
  }, [intent, state]);

  const openIntake = () => {
    window.open(
      "https://integratedwellness.jotform.com/261513760940051",
      "_blank",
      "noopener,noreferrer"
    );
  };

  const saveToNotion = useCallback(async () => {
    if (!reply || saveState === "saving") return;
    const name = memberName.trim();
    if (!name) {
      setSaveState("error");
      setSaveError("Please enter your name so this saves to your Notion record.");
      return;
    }
    setSaveState("saving");
    setSaveError("");
    try {
      const notes = `Intent: ${intent.trim()}\n\nCoach reply:\n${reply}`;
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/nova/save`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            member_name: name,
            source: "ChatGPT",
            summary_type: "journal",
            path: "Peak State Warm-Up",
            modalities: ["Peak State", "Focus / Imagery"],
            notes,
            disposition: "Warm-up completed — proceeding to intake",
          }),
        }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      if (data.notion_enabled && !data.success) {
        // Mongo persist DID succeed (HTTP 200); only Notion mirror failed.
        setSaveState("saved-local");
        setSaveError(data.error || "Notion sync temporarily unavailable.");
        return;
      }
      setSaveState("saved");
    } catch (err) {
      setSaveState("error");
      setSaveError(err.message || "Save failed.");
    }
  }, [reply, saveState, memberName, intent]);

  return (
    <section
      id="peak-state-warmup"
      className="mt-12 fade-up"
      data-testid="peak-state-warmup-section"
    >
      <div
        className="iw-card p-6 sm:p-8"
        style={{ borderTop: "3px solid var(--iw-accent-red)" }}
      >
        <div
          className="iw-mono uppercase mb-2"
          style={{
            color: "var(--iw-accent-red)",
            fontSize: "11px",
            letterSpacing: "0.24em",
            fontWeight: 600,
          }}
        >
          Warm-Up · GPT-5.6
        </div>
        <h3
          className="serif text-xl sm:text-2xl leading-tight mb-2"
          style={{ color: "var(--iw-primary)", fontWeight: 700 }}
        >
          Before you open the intake — what are you preparing for?
        </h3>
        <p
          className="text-sm sm:text-base leading-relaxed mb-4"
          style={{ color: "var(--iw-body)" }}
        >
          One sentence. A competition, a presentation, a hard conversation,
          or general focus. The coach will reflect it back and give you 2–3
          bullets to bring into the intake form.
        </p>

        <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3">
          <input
            type="text"
            value={intent}
            onChange={(e) => setIntent(e.target.value)}
            placeholder="e.g. I want to be calmer in courtroom cross-examinations"
            maxLength={500}
            disabled={state === "loading"}
            className="flex-1 px-4 py-3 text-base"
            style={{
              border: "1px solid var(--iw-border)",
              borderRadius: "2px",
              background: "#fff",
              color: "var(--iw-primary)",
              fontFamily: "'IBM Plex Sans', sans-serif",
              outline: "none",
            }}
            data-testid="peak-state-warmup-input"
          />
          <button
            type="submit"
            disabled={intent.trim().length < 3 || state === "loading"}
            className="iw-btn-primary"
            style={{
              opacity: intent.trim().length < 3 || state === "loading" ? 0.4 : 1,
              cursor: intent.trim().length < 3 ? "not-allowed" : "pointer",
            }}
            data-testid="peak-state-warmup-submit-btn"
          >
            {state === "loading" ? "Thinking…" : "Warm up ↗"}
          </button>
        </form>

        {state === "done" && reply && (
          <div
            className="mt-5 p-5"
            style={{
              background: "var(--iw-blue-tint)",
              border: "1px solid var(--iw-blue)",
              borderRadius: "2px",
              whiteSpace: "pre-wrap",
            }}
            data-testid="peak-state-warmup-reply"
          >
            <div
              className="iw-mono uppercase mb-2"
              style={{
                color: "var(--iw-blue)",
                fontSize: "10px",
                letterSpacing: "0.22em",
                fontWeight: 600,
              }}
            >
              From the Peak State Coach
            </div>
            <p
              className="text-[15px] leading-relaxed"
              style={{ color: "var(--iw-primary)" }}
            >
              {reply}
            </p>
            <div className="mt-4 flex flex-col gap-3">
              <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                <input
                  type="text"
                  value={memberName}
                  onChange={(e) => setMemberName(e.target.value)}
                  placeholder="Your name (to save to Notion)"
                  maxLength={100}
                  disabled={saveState === "saving" || saveState === "saved" || saveState === "saved-local"}
                  className="flex-1 px-3 py-2 text-sm"
                  style={{
                    border: "1px solid var(--iw-border)",
                    borderRadius: "2px",
                    background: "#fff",
                    color: "var(--iw-primary)",
                    fontFamily: "'IBM Plex Sans', sans-serif",
                    outline: "none",
                  }}
                  data-testid="peak-state-warmup-member-name-input"
                />
                {saveState !== "saved" && saveState !== "saved-local" && (
                  <button
                    type="button"
                    onClick={saveToNotion}
                    disabled={saveState === "saving"}
                    className="iw-btn-primary"
                    style={{
                      opacity: saveState === "saving" ? 0.5 : 1,
                      cursor: saveState === "saving" ? "wait" : "pointer",
                    }}
                    data-testid="peak-state-warmup-save-notion-btn"
                  >
                    {saveState === "saving"
                      ? "Saving…"
                      : saveState === "error"
                      ? "Retry save"
                      : "Save to Notion ↗"}
                  </button>
                )}
                {saveState === "saved" && (
                  <span
                    className="iw-mono uppercase inline-flex items-center"
                    style={{
                      color: "var(--iw-blue)",
                      fontSize: "10px",
                      letterSpacing: "0.22em",
                      fontWeight: 600,
                    }}
                    data-testid="peak-state-warmup-save-notion-confirm"
                  >
                    ✓ Saved to Notion
                  </span>
                )}
                {saveState === "saved-local" && (
                  <span
                    className="iw-mono uppercase inline-flex items-center"
                    style={{
                      color: "var(--iw-blue)",
                      fontSize: "10px",
                      letterSpacing: "0.22em",
                      fontWeight: 600,
                    }}
                    data-testid="peak-state-warmup-save-notion-local"
                  >
                    ✓ Saved to your record
                  </span>
                )}
              </div>
              {saveState === "saved-local" && (
                <span
                  className="text-[11px]"
                  style={{ color: "var(--iw-muted)" }}
                >
                  Notion sync is temporarily unavailable — your wellness team will still see this on their side once it reconnects.
                </span>
              )}
              {saveState === "error" && saveError && (
                <span
                  className="text-xs"
                  style={{ color: "var(--iw-accent-red)" }}
                  data-testid="peak-state-warmup-save-notion-error"
                  role="alert"
                >
                  {saveError}
                </span>
              )}
              <button
                type="button"
                onClick={openIntake}
                className="iw-btn-primary self-start"
                data-testid="peak-state-warmup-open-intake-btn"
              >
                Open the intake form <span aria-hidden="true">↗</span>
              </button>
            </div>
          </div>
        )}
        {state === "error" && (
          <p
            className="mt-3 text-sm"
            style={{ color: "var(--iw-accent-red)" }}
            data-testid="peak-state-warmup-error"
            role="alert"
          >
            Warm-up couldn&apos;t run just now — try again, or open the intake
            directly using the button in the hero above.
          </p>
        )}
      </div>
    </section>
  );
}
